import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Menu, Home, Map, Heart, MessageCircle, Star, Instagram, Mail, X, ArrowUpRight, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";

const navigation = [{
  name: "Accueil",
  href: "/",
  icon: Home,
  description: "Découvrez Roca Rio",
}, {
  name: "Mes excursions",
  href: "/excursions",
  icon: Map,
  description: "Toutes les aventures",
}, {
  name: "À propos",
  href: "/a-propos",
  icon: Heart,
  description: "Mission solidaire",
}];

interface HeaderProps {
  hideOnHero?: boolean;
}

export const Header = ({ hideOnHero = false }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visible, setVisible] = useState(!hideOnHero);
  const lastScrollY = useRef(0);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentY = window.scrollY;
          
          if (hideOnHero) {
            // On hero pages: show after scrolling past 80% of viewport OR scrolling up
            const pastHero = currentY > window.innerHeight * 0.8;
            const scrollingUp = currentY < lastScrollY.current - 5;
            setVisible(pastHero || (scrollingUp && currentY > 100));
          } else {
            // On other pages: hide on scroll down, show on scroll up
            const scrollingDown = currentY > lastScrollY.current + 5;
            const scrollingUp = currentY < lastScrollY.current - 5;
            
            if (scrollingDown && currentY > 80) {
              setVisible(false);
            } else if (scrollingUp || currentY < 80) {
              setVisible(true);
            }
          }
          
          lastScrollY.current = currentY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hideOnHero]);

  return (
    <>
      {!hideOnHero && <div className="h-14" />}
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 safe-top",
          visible
            ? "translate-y-0 opacity-100"
            : "-translate-y-full opacity-0 pointer-events-none",
          "bg-background/80 backdrop-blur-lg border-b border-border/30"
        )}
        role="banner"
      >
        <nav className="relative mx-auto flex max-w-7xl items-center justify-between py-1.5 px-4 lg:px-8" aria-label="Navigation principale">
          {/* Logo · compact */}
          <div className="flex lg:flex-1">
            <Link to="/" className="flex items-center transition-all duration-300 focus-visible-ring rounded-xl p-0.5" aria-label="Retour à l'accueil - Roca Rio">
              <img src="/roca-rio-logo.png" alt="Roca Rio" className="h-10 w-auto" />
            </Link>
          </div>
          
          {/* Mobile menu */}
          <div className="flex xl:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="inline-flex items-center justify-center rounded-md p-2 min-h-[44px] min-w-[44px] focus-visible-ring" aria-label="Ouvrir le menu de navigation" aria-expanded={mobileMenuOpen}>
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-full sm:max-w-md p-0 bg-roca-blue-dark text-white border-l-0 [&>button]:hidden overflow-hidden"
                id="mobile-menu"
              >
                {/* Decorative glows */}
                <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-roca-orange/15 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-primary/20 blur-3xl pointer-events-none" />

                <div className="relative flex flex-col h-full p-6 sm:p-8">
                  {/* Header row */}
                  <div className="flex items-center justify-between mb-10">
                    <Link
                      to="/"
                      onClick={() => setMobileMenuOpen(false)}
                      className="focus-visible-ring rounded-lg"
                      aria-label="Accueil Roca Rio"
                    >
                      <img src="/roca-rio-logo.png" alt="Roca Rio" className="h-10 w-auto" />
                    </Link>
                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors focus-visible-ring"
                      aria-label="Fermer le menu"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">Navigation</p>

                  {/* Editorial nav */}
                  <nav className="flex-1" aria-label="Navigation mobile">
                    <ul className="space-y-1" role="list">
                      {navigation.map((item, index) => {
                        const isActive = location.pathname === item.href;
                        return (
                          <li key={item.name} role="listitem">
                            <Link
                              to={item.href}
                              onClick={() => setMobileMenuOpen(false)}
                              aria-current={isActive ? "page" : undefined}
                              className={cn(
                                "group flex items-center justify-between gap-4 py-3.5 border-b border-white/10 transition-colors animate-fade-in focus-visible-ring rounded-sm",
                                isActive ? "text-roca-orange" : "text-white hover:text-roca-orange"
                              )}
                              style={{ animationDelay: `${index * 60}ms` }}
                            >
                              <div className="flex flex-col">
                                <span className="font-league uppercase text-2xl sm:text-3xl leading-[0.95] tracking-tight">
                                  {item.name}
                                </span>
                                <span className="text-xs text-white/50 mt-1">{item.description}</span>
                              </div>
                              <ArrowUpRight className="w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all flex-shrink-0" />
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>

                  {/* Footer block */}
                  <div className="mt-8 space-y-5 animate-fade-in" style={{ animationDelay: "350ms" }}>
                    <Button
                      asChild
                      size="lg"
                      className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full h-12 shadow-lg"
                    >
                      <a
                        href="https://wa.me/5521976795050?text=Bonjour%20Cl%C3%A9a%20!"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Écrire à Cléa sur WhatsApp
                      </a>
                    </Button>

                    <div className="flex items-center justify-between pt-2">
                      <div className="flex items-center gap-3">
                        <a
                          href="https://instagram.com/roca.rio_"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-roca-orange hover:border-roca-orange transition-colors"
                          aria-label="Instagram"
                        >
                          <Instagram className="w-4 h-4" />
                        </a>
                        <a
                          href="mailto:contact.rocario@gmail.com"
                          className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-roca-orange hover:border-roca-orange transition-colors"
                          aria-label="Email"
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center justify-end gap-1 text-roca-orange text-xs font-semibold">
                          <Star className="w-3 h-3 fill-roca-orange" />
                          <span>5/5 · Google</span>
                        </div>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5">Guide francophone</p>
                      </div>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
          
          {/* Desktop nav · refined */}
          <div className="hidden xl:flex xl:items-center xl:gap-x-1 xl:ml-auto xl:mr-6" role="navigation" aria-label="Navigation desktop">
            {navigation.map(item => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "relative text-sm font-semibold px-3 py-1.5 rounded-lg transition-all duration-200 focus-visible-ring",
                    isActive
                      ? "text-primary"
                      : "text-foreground/70 hover:text-foreground"
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.name}
                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-primary rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>
          
          <div className="hidden xl:flex xl:items-center">
            <Button asChild size="sm" className="bg-primary hover:bg-secondary text-primary-foreground transition-all duration-200 h-8 px-4 text-xs font-semibold focus-visible-ring rounded-full uppercase tracking-wide">
              <Link to="/contact">
                C'est parti !
              </Link>
            </Button>
          </div>
        </nav>
      </header>
    </>
  );
};
